import type { Ticket } from "../lib/useKitchenTickets";
import { ageSeconds, channelLabel, earlierDay, fmtAge, pad2 } from "../lib/format";

// Amber from 6 minutes, red from 12 — the same thresholds the kitchen has always used.
function time(ticket: Ticket, seconds: number) {
  if (ticket.kitchenStatus === "ready") return "";
  return seconds >= 720 ? " is-late" : seconds >= 360 ? " is-warn" : "";
}

const STATE = { new: "new", preparing: "cook", ready: "ready" } as const;

const Icon = ({ name, size, fill }: { name: string; size?: number; fill?: boolean }) => (
  <span className={`ms${fill ? " fill" : ""}`} aria-hidden="true" style={size ? { fontSize: size } : undefined}>
    {name}
  </span>
);

export interface TicketCardProps {
  ticket: Ticket;
  now: Date;
  justArrived: boolean;
  onBump: (id: string) => void;
  onRecall: (id: string) => void;
  onToggleItem: (id: string, itemId: string) => void;
}

/** The kitchen ticket from the component library (canvas lib-08), as on the KDS boards: head by state, the timer by time, a blue flag for the first seconds. */
export function TicketCard({ ticket: t, now, justArrived, onBump, onRecall, onToggleItem }: TicketCardProps) {
  const seconds = ageSeconds(t.createdAt, now);
  const readyCount = t.items.filter((i) => i.ready).length;
  const where = t.isDineIn ? `Table ${pad2(t.tableNumber ?? 0)}` : "Pickup";
  const who = t.isDineIn ? t.serverName ?? channelLabel(t.source) : `${t.customerName ?? "Pickup"}, from ${channelLabel(t.source)}`;
  const cooking = t.kitchenStatus === "preparing";
  const ready = t.kitchenStatus === "ready";
  const madeOn = earlierDay(t.createdAt, now);

  return (
    <article className={`c-ticket c-kt c-kt--${STATE[t.kitchenStatus]}${time(t, seconds)}`} data-ticket={t.id} aria-label={`Order ${t.orderNumber}`}>
      {justArrived && (
        <div className="c-ticket__flag">
          <Icon name="notifications_active" size={15} fill />
          NEW ORDER
        </div>
      )}
      {madeOn && (
        <div className="c-ticket__flag c-ticket__flag--old">
          <Icon name="history" size={15} />
          From {madeOn}
        </div>
      )}
      <div className="c-ticket__stub">
        <span className="c-ticket__no">#{t.orderNumber}</span>
        <span className="c-kt__where">
          {where}
          <small>{who}</small>
        </span>
        <span className="c-kt__timer">
          <Icon name={ready ? "check_circle" : "timer"} fill={ready} />
          {fmtAge(seconds)}
        </span>
      </div>
      <div className="c-kt__perf" aria-hidden="true" />
      {t.nudged && !ready && (
        <div className="c-kt__nudge" role="status">
          <Icon name="notifications_active" />
          Counter is asking for this
        </div>
      )}
      <div className="c-ticket__body">
        {(cooking || t.pendingSync) && (
          <div className="c-kt__prog">
            <span>{cooking ? `${readyCount} of ${t.items.length} done` : ""}</span>
            {t.pendingSync && <span title="Saved here, sending to the server">Syncing</span>}
          </div>
        )}
        {t.orderNotes && (
          <div className="c-kt__note">
            <Icon name="sticky_note_2" />
            {t.orderNotes}
          </div>
        )}
        {/* Each line is its own tap, so the floor can serve it while the rest cooks. A new ticket has no boxes yet. */}
        {t.items.map((item) => (
          <button key={item.id} className={`c-kt__item${item.ready || ready ? " is-done" : ""}`} aria-pressed={item.ready} disabled={item.served} onClick={() => onToggleItem(t.id, item.id)}>
            {cooking && (
              <span className="c-check" aria-checked={item.ready} aria-hidden="true">
                <Icon name="check" />
              </span>
            )}
            <b>{item.qty}×</b>
            <span>
              {item.name}
              {item.variantLabel && <small>{item.variantLabel}</small>}
              {item.notes && <small className="is-alert">{item.notes}</small>}
            </span>
            {item.served && <em>Served</em>}
          </button>
        ))}
      </div>
      <div className="c-ticket__tear" />
      <div className="c-ticket__foot">
        {t.kitchenStatus === "new" && (
          <button className="c-key c-key--coral c-key--block" onClick={() => onBump(t.id)}>
            Start
          </button>
        )}
        {cooking && (
          <>
            <button className="c-key c-key--line c-key--icon" aria-label="Back to new" onClick={() => onRecall(t.id)}>
              <Icon name="undo" />
            </button>
            <button className="c-key c-key--block" onClick={() => onBump(t.id)}>
              Ready
            </button>
          </>
        )}
        {ready && (
          <button className="c-key c-key--line c-key--block" aria-label="Back to cooking" onClick={() => onRecall(t.id)}>
            <Icon name="undo" />
            Recall
          </button>
        )}
      </div>
    </article>
  );
}
